import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Trash2, MessageCircle, Check, X } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { AdminHeader } from "../../components/admin/AdminLayout";
import { thisMonth, registerPayment } from "../../components/admin/RegisterPayment";
import { api, brl, dateBR, downloadFile, formatError, monthLabel, whatsappChargeLink, whatsappReminderLink } from "../../lib/api";

const METHOD = { pix: "Pix", card: "Cartão", dinheiro: "Dinheiro" };

function DueList({ rows, empty, onPay, testId }) {
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white divide-y divide-neutral-100" data-testid={testId}>
      {rows.length === 0 && <p className="p-6 text-sm text-neutral-500">{empty}</p>}
      {rows.map((u) => (
        <div key={u.member_id} className="p-4 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="font-medium"><span className="font-mono text-[#166534] mr-2">{u.cadastro_number}</span>{u.member_name}</p>
            <p className="text-xs text-neutral-500">
              {u.telefone} · venc. {dateBR(u.due_date)} ·{" "}
              {u.is_past_due ? <span className="text-rose-600 font-semibold">Vencida há {u.days_overdue} dia(s)</span> : `Vence em ${u.days_until_due} dia(s)`}
              {u.awaiting && <span className="ml-1 text-amber-700 font-semibold">· Pix informado</span>}
            </p>
          </div>
          <div className="flex gap-2">
            {u.is_past_due && (
              <a href={whatsappChargeLink(u.member_name, u.telefone, u.due_date, u.days_overdue)} target="_blank" rel="noreferrer">
                <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800" data-testid={`whatsapp-charge-${u.member_id}`}>
                  <MessageCircle className="w-4 h-4 mr-1" /> Cobrar no WhatsApp
                </Button>
              </a>
            )}
            {!u.is_past_due && u.days_until_due != null && u.days_until_due <= 3 && (
              <a href={whatsappReminderLink(u.member_name, u.telefone, u.due_date)} target="_blank" rel="noreferrer">
                <Button size="sm" variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-800" data-testid={`whatsapp-remind-${u.member_id}`}>
                  <MessageCircle className="w-4 h-4 mr-1" /> Lembrar no WhatsApp
                </Button>
              </a>
            )}
            <Button size="sm" onClick={() => onPay(u.member_id)} className="bg-[#166534] hover:bg-[#14532d] hover:text-white text-white" data-testid={`mark-paid-${u.member_id}`}>
              Registrar pagamento
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

function Summary({ b }) {
  const items = [
    ["Aguardando confirmação", b.awaiting.length],
    ["Recebido no mês", brl(b.received)],
    ["Vencidos", `${b.overdue.length} · ${brl(b.overdue.length * b.fee)}`],
    ["Vencem em 7 dias", b.upcoming.length],
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="billing-summary">
      {items.map(([l, v]) => (
        <div key={l} className="rounded-2xl border border-neutral-200 bg-white p-4">
          <p className="text-xs font-semibold text-neutral-500 tracking-wider uppercase">{l}</p>
          <p className="font-display text-xl font-extrabold mt-1">{v}</p>
        </div>
      ))}
    </div>
  );
}

export default function Payments() {
  const [month, setMonth] = useState(thisMonth());
  const [b, setB] = useState(null);
  const [paidOn, setPaidOn] = useState({});

  const load = useCallback(async () => {
    if (!month) return;
    try {
      const { data } = await api.get("/admin/billing", { params: { month } });
      setB(data);
    } catch (err) {
      toast.error(formatError(err));
    }
  }, [month]);

  useEffect(() => {
    load();
  }, [load]);

  async function pay(id) {
    if (await registerPayment(id, "pix")) load();
  }

  async function confirm(p) {
    try {
      await api.post(`/admin/payments/${p.id}/confirm`, { paid_on: paidOn[p.id] || undefined });
      toast.success(`Recebimento confirmado · ${p.member_name}`);
      load();
    } catch (err) {
      toast.error(formatError(err));
    }
  }

  async function reverse(p) {
    if (!window.confirm(`${p.status === "awaiting" ? "Recusar" : "Estornar"} pagamento de ${p.member_name}?`)) return;
    try {
      await api.delete(`/admin/payments/${p.id}`);
      toast.success(p.status === "awaiting" ? "Pagamento recusado" : "Pagamento estornado");
      load();
    } catch (err) {
      toast.error(formatError(err));
    }
  }

  return (
    <div data-testid="admin-payments-page">
      <AdminHeader title="Mensalidades" subtitle="R$ 30,00 a cada 30 dias · vencimentos, confirmações e pagamentos">
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44 bg-white" data-testid="billing-month" title="Mês dos pagamentos recebidos" />
        <Button variant="outline" onClick={() => downloadFile("/admin/export/payments.csv", `pagamentos-${month}.csv`, { month })} data-testid="export-month-csv">
          <Download className="w-4 h-4 mr-2" /> CSV do mês
        </Button>
      </AdminHeader>
      {b && (
        <>
          <Summary b={b} />
          <Tabs defaultValue={b.awaiting.length ? "awaiting" : "overdue"}>
            <TabsList className="flex-wrap h-auto">
              <TabsTrigger value="awaiting" data-testid="tab-awaiting">Aguardando confirmação ({b.awaiting.length})</TabsTrigger>
              <TabsTrigger value="overdue" data-testid="tab-overdue">Vencidos ({b.overdue.length})</TabsTrigger>
              <TabsTrigger value="upcoming" data-testid="tab-upcoming">A vencer ({b.upcoming.length})</TabsTrigger>
              <TabsTrigger value="paid" data-testid="tab-paid">Pagos em {monthLabel(month)} ({b.paid.length})</TabsTrigger>
            </TabsList>
            <TabsContent value="awaiting">
              <div className="rounded-2xl border border-neutral-200 bg-white divide-y divide-neutral-100" data-testid="awaiting-list">
                {b.awaiting.length === 0 && <p className="p-6 text-sm text-neutral-500">Nenhum pagamento aguardando confirmação.</p>}
                {b.awaiting.map((p) => (
                  <div key={p.id} className="p-4 flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <p className="font-medium"><span className="font-mono text-[#166534] mr-2">{p.cadastro_number}</span>{p.member_name}</p>
                      <p className="text-xs text-neutral-500">
                        {METHOD[p.method] || p.method} · {brl(p.amount)} · informado em {dateBR(p.created_at)}
                        {p.member_status === "pending" && <span className="ml-1 text-amber-700 font-semibold">· 1ª mensalidade (ativa o cadastro)</span>}
                      </p>
                    </div>
                    <div className="flex gap-2 items-center flex-wrap">
                      <Input
                        type="date"
                        max={new Date().toLocaleDateString("en-CA")}
                        value={paidOn[p.id] || ""}
                        onChange={(e) => setPaidOn({ ...paidOn, [p.id]: e.target.value })}
                        className="w-40 h-9"
                        title="Data do pagamento (vazio = hoje)"
                        data-testid={`paid-on-${p.id}`}
                      />
                      <Button size="sm" variant="outline" className="border-rose-200 text-rose-700 hover:bg-rose-50 hover:text-rose-800" onClick={() => reverse(p)} data-testid={`reject-${p.id}`}>
                        <X className="w-4 h-4 mr-1" /> Recusar
                      </Button>
                      <Button size="sm" onClick={() => confirm(p)} className="bg-[#166534] hover:bg-[#14532d] hover:text-white text-white" data-testid={`confirm-${p.id}`}>
                        <Check className="w-4 h-4 mr-1" /> Confirmar recebimento
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
            <TabsContent value="overdue">
              <DueList rows={b.overdue} empty="Nenhuma mensalidade vencida." onPay={pay} testId="overdue-list" />
            </TabsContent>
            <TabsContent value="upcoming">
              <DueList rows={b.upcoming} empty="Nenhum vencimento nos próximos 7 dias." onPay={pay} testId="upcoming-list" />
            </TabsContent>
            <TabsContent value="paid">
              <div className="rounded-2xl border border-neutral-200 bg-white overflow-x-auto">
                <table className="w-full text-sm" data-testid="paid-table">
                  <thead className="text-left bg-neutral-50 text-neutral-600">
                    <tr>{["Cadastro", "Nome", "Pago em", "Cobre até", "Método", "Valor", "Registro", ""].map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {b.paid.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-neutral-500">Nenhum pagamento.</td></tr>}
                    {b.paid.map((p) => (
                      <tr key={p.id} className="border-t border-neutral-100">
                        <td className="px-4 py-3 font-mono text-[#166534]">{p.cadastro_number}</td>
                        <td className="px-4 py-3">{p.member_name}</td>
                        <td className="px-4 py-3">{dateBR(p.paid_at)}</td>
                        <td className="px-4 py-3">{dateBR(p.covers_until)}</td>
                        <td className="px-4 py-3">{METHOD[p.method] || p.method}</td>
                        <td className="px-4 py-3">{brl(p.amount)}</td>
                        <td className="px-4 py-3 text-xs text-neutral-500">{p.registered_by === "admin" ? "Admin" : "Associado"}</td>
                        <td className="px-4 py-3 text-right">
                          <button onClick={() => reverse(p)} className="text-rose-600 hover:text-rose-800" title="Estornar" data-testid={`reverse-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
